import {
    ApiContentSection,
    ApiSectionType,
    isHtmlSectionDesktopReady,
} from '@storefronts/common-types'

/**
 * Section types that have been migrated to read their own container width
 * [sc-36106].
 *
 * A section not listed here is held at the legacy column width, so it always
 * renders at a size it was designed for rather than having its phone design
 * stretched across the page. Listing a type is the last step of migrating it,
 * and the only per-section state the layout keeps.
 */
export const DESKTOP_READY_SECTIONS: ReadonlySet<ApiSectionType> =
    new Set<ApiSectionType>([
        // Flow content: body copy holds to the prose measure, flush to the
        // section's leading edge, and background bands span the page.
        ApiSectionType.HEADER_TEXT,
        ApiSectionType.BODY_TEXT,
        ApiSectionType.RICH_TEXT,
        ApiSectionType.ACCORDION,
        ApiSectionType.SPACER,
        ApiSectionType.CTA_BUTTON,
        ApiSectionType.REVIEWS_SUMMARY,
        ApiSectionType.AI_REVIEWS_SUMMARY,
        ApiSectionType.PLP,
        ApiSectionType.PERSONALIZED_PLP,
        ApiSectionType.PRODUCT_CARD,
        ApiSectionType.COLLECTIONS_SECTION,
        ApiSectionType.UPSELL_BOX,
        ApiSectionType.CAROUSEL,
        ApiSectionType.VIDEO_CAROUSEL,
        ApiSectionType.REVIEWS_CAROUSEL,
        ApiSectionType.REVIEWS_IMAGES,
        ApiSectionType.REVIEWS_SECTION,
        ApiSectionType.INLINE_PDP,
        // HTML is decided per section, not per type: see isDesktopReadySection.
    ])

/**
 * HTML renders author-written markup in a shadow DOM that cannot be restyled
 * from here, so it is desktop-ready only when it was written for the section
 * container — which the backend marks on fragments the model authored under
 * the container-aware contract [sc-36409]. Older markup keeps its column.
 */
export const isDesktopReadySection = (section: ApiContentSection): boolean =>
    section.type === ApiSectionType.HTML
        ? isHtmlSectionDesktopReady(section.html)
        : DESKTOP_READY_SECTIONS.has(section.type)

/**
 * Desktop-ready HTML's container bleeds to the page so authored backgrounds
 * can reach the edges. The authoring contract holds copy and grids to the
 * 1200px column. Unstamped HTML and every other type stay in the column.
 */
export const sectionBleedsToPage = (section: ApiContentSection): boolean =>
    section.type === ApiSectionType.HTML && isDesktopReadySection(section)
